import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X, ChevronDown, ChevronRight, Shield, BookOpen, Bug, Lightbulb, PanelLeft, PanelRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import BugReportInlineForm from './BugReportInlineForm';
import FeatureRequestInlineForm from './FeatureRequestInlineForm';
import { useVisite } from './visite/VisiteController';
import { useAideContextuelle } from './AideContextuelle';
import { VAR_DROITE, VAR_GAUCHE } from './aideAncree';

// ── Panneau ANCRÉ (28/10/2026, demande William) ──────────────────────────────
// Sur un écran large, l'aide prend sa place à côté de la page (la page se resserre, rien n'est
// recouvert), dans une colonne qu'on élargit à la souris, à droite ou à gauche ; largeur et côté
// sont mémorisés par utilisateur. Sur un écran étroit, comportement d'avant : panneau par-dessus.
// La page se resserre par deux variables CSS posées sur <html> (lues par <main> dans App.js) :
// glisser la poignée ne re-rend donc que le panneau, jamais la page.
export const ANCRAGE_LARGEUR_MIN_ECRAN = 1025;   // au-dessous : panneau par-dessus (comme la barre latérale étroite)
export const AIDE_LARGEUR_MIN = 320;
// Plancher de la page quand l'aide s'ANCRE : 785 px, la largeur qu'avait la page avant l'aide ancrée (et
// avant le rail droit) sur le plus petit écran où l'on ancre (1025 − 240 de barre latérale). Des écrans
// calculent leur disposition sur la largeur de la FENÊTRE (grilles fixes de PagesIaPage, AgentScriptsPage…).
// Les barres fixes se déduisent de la fenêtre AVANT ce plancher : barre latérale à gauche, rail droit
// (40 px, 12 replié) à droite. S'il ne reste pas 785 px de page ET 320 px d'aide, l'aide repasse par-dessus
// au lieu de comprimer la page : avec le rail déplié et la barre latérale ouverte, il faut donc une fenêtre
// d'au moins 1385 px (785 + 320 + 240 + 40) au lieu de 1345. Ce plancher ne vaut que pour l'ancrage : le rail
// seul, sur un écran de 1025 px, laisse 745 px à la page (1025 − 240 − 40), comme toute barre fixe.
export const PAGE_LARGEUR_MIN = 785;
const AIDE_LARGEUR_MAX = 1100;
const PAS_CLAVIER = 32;
export { VAR_DROITE, VAR_GAUCHE };

/** Largeur par défaut : plus grande sur grand écran (30 % de la fenêtre, entre 380 et 720 px). */
export function largeurParDefaut(largeurEcran) {
  return Math.round(Math.min(720, Math.max(380, largeurEcran * 0.3)));
}

/** Vrai si l'écran laisse à la page ses 785 px ET une aide d'au moins 320 px (barre latérale déduite). */
export function peutAncrer(largeurEcran, decalage = 0) {
  return largeurEcran >= ANCRAGE_LARGEUR_MIN_ECRAN && largeurEcran - decalage - PAGE_LARGEUR_MIN >= AIDE_LARGEUR_MIN;
}

export function bornerLargeur(largeur, largeurEcran, decalage = 0) {
  const max = Math.max(AIDE_LARGEUR_MIN, Math.min(AIDE_LARGEUR_MAX, largeurEcran - decalage - PAGE_LARGEUR_MIN));
  return Math.round(Math.max(AIDE_LARGEUR_MIN, Math.min(max, largeur)));
}

export function cleReglage(user) {
  return `meteris_help_dock:${user?.id || user?.email || 'anonyme'}`;
}

function lireReglage(cle) {
  try {
    const brut = JSON.parse(window.localStorage.getItem(cle) || 'null');
    if (!brut || typeof brut !== 'object') return { cote: 'droite', largeur: null };
    return {
      cote: brut.cote === 'gauche' ? 'gauche' : 'droite',
      largeur: Number.isFinite(brut.largeur) ? brut.largeur : null,
    };
  } catch {
    return { cote: 'droite', largeur: null };
  }
}

function ecrireReglage(cle, reglage) {
  try { window.localStorage.setItem(cle, JSON.stringify(reglage)); } catch { /* stockage indisponible : réglage de session */ }
}

function poserMarges(ancre, cote, largeur) {
  const st = document.documentElement.style;
  st.setProperty(VAR_DROITE, ancre && cote === 'droite' ? `${largeur}px` : '0px');
  st.setProperty(VAR_GAUCHE, ancre && cote === 'gauche' ? `${largeur}px` : '0px');
}

function retirerMarges() {
  const st = document.documentElement.style;
  st.removeProperty(VAR_DROITE);
  st.removeProperty(VAR_GAUCHE);
  delete document.documentElement.dataset.aideRedim;
}

/** Route d'aide : exacte, puis motif à paramètres (« /reports/conformite/:clientId »), puis parent. */
export function trouverAide(helpData, pathname) {
  if (!helpData) return null;
  if (helpData[pathname]) return helpData[pathname];
  const segments = pathname.split('/');
  let meilleur = null;
  let meilleursParams = Infinity;
  Object.keys(helpData).forEach((motif) => {
    if (!motif.includes(':')) return;
    const m = motif.split('/');
    if (m.length !== segments.length) return;
    let params = 0;
    const ok = m.every((seg, i) => {
      if (seg.startsWith(':')) { params += 1; return segments[i] !== ''; }
      return seg === segments[i];
    });
    if (ok && params < meilleursParams) { meilleur = motif; meilleursParams = params; }
  });
  if (meilleur) return helpData[meilleur];
  const parent = segments.slice(0, -1).join('/');
  if (helpData[parent]) return helpData[parent];
  return null;
}

/**
 * HelpPanel — Bouton ? flottant + panneau lateral d'aide contextuelle.
 *
 * Usage :
 *   <HelpPanel helpData={assistantHelp} />
 *
 * helpData = { '/route': { title, user: [...], admin: [...] } }
 *
 * Le panneau affiche l'aide correspondant a la route courante.
 * Les sections admin ne sont visibles que pour les superadmin.
 *
 * Ancrage (28/10/2026) : sur écran large (place pour 785 px de page + 320 px d'aide), le panneau prend
 * sa place à côté de la page (à droite ou à gauche, largeur réglable à la poignée, mémorisés par
 * utilisateur) et reste ouvert d'une page à l'autre ; sinon il reste par-dessus et se ferme à la navigation. Une page peut pousser une aide CONTEXTUELLE (AideContextuelle.js),
 * affichée en tête ; l'aide de la route reste alors accessible, repliée.
 *
 * Rail droit (28/10/2026, RailDroit.js) : `decalageDroite` = sa largeur. L'aide ancrée à droite se range
 * à GAUCHE du rail, et la place gardée à la page (785 px) se compte rail déduit, comme la barre latérale.
 * `conteneurBouton` = l'emplacement que le rail prête au bouton ? : le bouton y est rendu (portail), simple
 * bouton cliquable au clavier ; sans emplacement (rail replié, petit écran), il reste flottant et déplaçable.
 * Dans les deux cas il porte data-tour="help-button" (dernière étape de « Premiers pas »).
 *
 * Visites guidées (05/10/2026) : lues dans le contexte de VisiteController (useVisite) —
 * « Premiers pas » (ou « Reprendre… ») pour un interne, « Visite de cette page » seulement si la
 * page en a une. Hors du contrôleur, aucun de ces deux boutons.
 */
export default function HelpPanel({ helpData, decalageGauche = 0, decalageDroite = 0, conteneurBouton = null }) {
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [featureOpen, setFeatureOpen] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();
  const visite = useVisite();
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('meteris_help_button_pos') || 'null');
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
        // Reset si position hors viewport (résolution changée, drag accidentel)
        const margin = 40;
        const inView = saved.x >= 0 && saved.x <= window.innerWidth - margin
                    && saved.y >= 0 && saved.y <= window.innerHeight - margin;
        if (inView) return saved;
        try { localStorage.removeItem('meteris_help_button_pos'); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    return null; // null = position par défaut (bottom: 100, right: 20)
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, posX: 0, posY: 0, didMove: false });
  const buttonRef = useRef(null);

  const isSuperAdmin = user?.role === 'superadmin';
  const { aide: aideContextuelle, ouverture } = useAideContextuelle();
  const [generaleOuverte, setGeneraleOuverte] = useState(false);

  // ── Ancrage : côté + largeur mémorisés par utilisateur ──
  const cle = cleReglage(user);
  const [reglage, setReglage] = useState(() => lireReglage(cle));
  useEffect(() => { setReglage(lireReglage(cle)); }, [cle]);
  const [largeurEcran, setLargeurEcran] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    const onResize = () => setLargeurEcran(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [redim, setRedim] = useState(false);
  // Ce que les barres (latérale à gauche, rail à droite) prennent déjà à la largeur de la fenêtre.
  const barres = (decalageGauche || 0) + (decalageDroite || 0);
  const ancrable = peutAncrer(largeurEcran, barres);
  const ancre = open && ancrable;
  const ancreRef = useRef(ancre);
  ancreRef.current = ancre;
  const decalage = reglage.cote === 'gauche' ? (decalageGauche || 0) : 0;
  const largeur = bornerLargeur(reglage.largeur ?? largeurParDefaut(largeurEcran), largeurEcran, barres);

  // La page se resserre (variables CSS lues par <main>) ; fermer ou démonter rend toute la largeur.
  useEffect(() => { poserMarges(ancre, reglage.cote, largeur); }, [ancre, reglage.cote, largeur]);
  useEffect(() => retirerMarges, []);

  const changerReglage = useCallback((maj) => {
    setReglage((prec) => {
      const suivant = { ...prec, ...maj };
      ecrireReglage(cle, suivant);
      return suivant;
    });
  }, [cle]);

  // Glisser la poignée : la largeur suit la souris, mémorisée au relâchement.
  useEffect(() => {
    if (!redim) return undefined;
    document.documentElement.dataset.aideRedim = '1';
    let derniere = null;
    const onMove = (e) => {
      const brute = reglage.cote === 'droite' ? window.innerWidth - (decalageDroite || 0) - e.clientX : e.clientX - decalage;
      derniere = bornerLargeur(brute, window.innerWidth, barres);
      setReglage((prec) => ({ ...prec, largeur: derniere }));
    };
    const onUp = () => {
      setRedim(false);
      if (derniere !== null) changerReglage({ largeur: derniere });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      delete document.documentElement.dataset.aideRedim;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [redim, reglage.cote, decalage, decalageDroite, barres, changerReglage]);

  const onPoigneeClavier = (e) => {
    const vers = { ArrowLeft: -1, ArrowRight: 1 }[e.key];
    if (!vers) return;
    e.preventDefault();
    const sens = reglage.cote === 'droite' ? -vers : vers;   // vers l'extérieur du panneau = plus large
    changerReglage({ largeur: bornerLargeur(largeur + sens * PAS_CLAVIER, largeurEcran, barres) });
  };

  // Drag and drop du bouton ? (glisser = déplacer, clic court = ouvrir)
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragRef.current.didMove = true;
      const maxX = window.innerWidth - 40;
      const maxY = window.innerHeight - 40;
      setPos({
        x: Math.max(0, Math.min(maxX, dragRef.current.posX + dx)),
        y: Math.max(0, Math.min(maxY, dragRef.current.posY + dy)),
      });
    };
    const onUp = () => {
      setDragging(false);
      if (dragRef.current.didMove) {
        try {
          const rect = buttonRef.current?.getBoundingClientRect();
          if (rect) {
            localStorage.setItem('meteris_help_button_pos', JSON.stringify({ x: rect.left, y: rect.top }));
          }
        } catch { /* ignore */ }
      } else {
        // Clic gauche court (pas de drag) → toggle l'aide. Le clic droit
        // reste supporté en parallèle pour la compatibilité.
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // clic gauche uniquement
    const rect = buttonRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      posX: pos?.x ?? rect.left,
      posY: pos?.y ?? rect.top,
      didMove: false,
    };
    setDragging(true);
    e.preventDefault();
  };

  const resetHelpPosition = () => {
    setPos(null);
    try { localStorage.removeItem('meteris_help_button_pos'); } catch { /* ignore */ }
  };

  // Route d'aide : exacte, puis motif à paramètres quel que soit son nom (« :id », « :clientId »…), puis parent.
  const help = trouverAide(helpData, pathname);

  // Changement de page : l'aide ANCRÉE reste ouverte (elle montre l'aide de la nouvelle page) ; par-dessus
  // (écran étroit), elle se ferme comme avant. Les formulaires bug / idée repartent fermés dans les deux cas.
  useEffect(() => {
    if (!ancreRef.current) setOpen(false);
    setReportOpen(false); setFeatureOpen(false);
  }, [pathname]);

  // Une page demande l'ouverture (ex. bouton « ? » d'une ligne du rapport Conformité).
  useEffect(() => { if (ouverture > 0) setOpen(true); }, [ouverture]);
  // Nouvelle aide contextuelle : l'aide générale de l'écran repart repliée.
  useEffect(() => { setGeneraleOuverte(false); }, [aideContextuelle?.cle]);

  // Le bouton flottant est TOUJOURS rendu pour permettre le signalement de bug
  // depuis n'importe quelle page (même celles sans aide contextuelle définie).

  return (
    <>
      {conteneurBouton ? createPortal(
        <button
          type="button"
          data-tour="help-button"
          data-bug-capture-skip="true"
          data-testid="rail-aide"
          onClick={() => setOpen(prev => !prev)}
          aria-expanded={open}
          aria-label="Aide de la page et signalement de bug"
          title="Aide de la page / signaler un bug"
          style={{
            width: 32, height: 32, borderRadius: '50%', padding: 0, cursor: 'pointer',
            background: open ? 'var(--primary)' : 'var(--surface)',
            color: open ? '#fff' : 'var(--primary)',
            border: `2px solid ${open ? 'var(--primary)' : 'var(--border-light)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {open ? <X size={15} aria-hidden="true" /> : <HelpCircle size={17} aria-hidden="true" />}
        </button>,
        conteneurBouton,
      ) : ( /* Bouton flottant (hors rail) — glisser = déplacer | clic court ou clic droit = ouvrir */
        <button
          ref={buttonRef}
          data-tour="help-button"
          data-bug-capture-skip="true"
          onMouseDown={handleMouseDown}
          onContextMenu={(e) => {
            e.preventDefault();
            if (!dragRef.current.didMove) setOpen(prev => !prev);
          }}
          title="Cliquer pour ouvrir l'aide / signaler un bug | Glisser pour déplacer"
          style={{
            position: 'fixed',
            ...(pos
              ? { left: pos.x, top: pos.y }
              : { bottom: 100, right: 20 + (decalageDroite || 0) }),   // à gauche du rail droit
            zIndex: 80,
            width: 36, height: 36, borderRadius: '50%',
            background: open ? 'var(--primary)' : 'var(--surface)',
            color: open ? '#fff' : 'var(--primary)',
            border: `2px solid ${open ? 'var(--primary)' : 'var(--border-light)'}`,
            cursor: dragging ? 'grabbing' : 'grab',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--shadow-lg)',
            transition: dragging ? 'none' : 'all 0.2s ease',
            opacity: dragging ? 0.7 : 1,
            padding: 0,
          }}
        >
          {open ? <X size={16} /> : <HelpCircle size={18} />}
        </button>
      )}

      {/* Panel — ancré à côté de la page (écran large) ou par-dessus (écran étroit) */}
      {open && (
          <div
            data-bug-capture-skip="true"
            data-testid="aide-panneau"
            data-mode={ancre ? 'ancre' : 'superpose'}
            data-cote={ancre ? reglage.cote : 'droite'}
            role="complementary"
            aria-label="Aide"
            style={ancre ? {
              position: 'fixed', top: 0, bottom: 0, zIndex: 90,
              ...(reglage.cote === 'droite' ? { right: decalageDroite || 0 } : { left: decalage }),
              width: largeur,
              background: 'var(--surface)',
              ...(reglage.cote === 'droite'
                ? { borderLeft: '1px solid var(--border-light)' }
                : { borderRight: '1px solid var(--border-light)' }),
              boxShadow: 'var(--shadow-lg)',
              display: 'flex', flexDirection: 'column',
            } : {
              position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 90,
              width: 380, maxWidth: '40vw',
              background: 'var(--surface)', borderLeft: '1px solid var(--border-light)',
              boxShadow: 'var(--shadow-xl)',
              display: 'flex', flexDirection: 'column',
              animation: 'slide-in-right 0.2s ease-out',
            }}
          >
            {/* Poignée : glisser pour élargir / rétrécir ; flèches du clavier ; double-clic = largeur par défaut */}
            {ancre && (
              <div
                data-testid="aide-poignee"
                role="separator"
                aria-orientation="vertical"
                aria-label="Redimensionner l'aide"
                aria-valuenow={largeur}
                aria-valuemin={AIDE_LARGEUR_MIN}
                tabIndex={0}
                title="Glisser pour élargir ou rétrécir l'aide (double-clic : largeur par défaut)"
                onMouseDown={(e) => { if (e.button === 0) { e.preventDefault(); setRedim(true); } }}
                onDoubleClick={() => changerReglage({ largeur: null })}
                onKeyDown={onPoigneeClavier}
                style={{
                  position: 'absolute', top: 0, bottom: 0, width: 8, zIndex: 1, cursor: 'col-resize',
                  ...(reglage.cote === 'droite' ? { left: -4 } : { right: -4 }),
                  background: redim ? 'rgba(91,163,230,0.45)' : 'transparent',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(91,163,230,0.35)'; }}
                onMouseLeave={(e) => { if (!redim) e.currentTarget.style.background = 'transparent'; }}
              />
            )}
            {/* Header */}
            <div style={{
              background: 'var(--primary)', color: '#fff', padding: '16px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BookOpen size={18} />
                <span style={{ fontWeight: 700, fontSize: 14 }}>{help?.title || 'Aide & Signalement'}</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {ancre && (
                  <button
                    type="button"
                    data-testid="aide-cote"
                    onClick={() => changerReglage({ cote: reglage.cote === 'droite' ? 'gauche' : 'droite' })}
                    title={reglage.cote === 'droite' ? "Placer l'aide à gauche de la page" : "Placer l'aide à droite de la page"}
                    aria-label={reglage.cote === 'droite' ? "Placer l'aide à gauche" : "Placer l'aide à droite"}
                    style={{
                      background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 6, color: '#fff', cursor: 'pointer', padding: 4, display: 'flex',
                    }}
                  >
                    {reglage.cote === 'droite' ? <PanelLeft size={14} /> : <PanelRight size={14} />}
                  </button>
                )}
                {!conteneurBouton && <button
                  onClick={resetHelpPosition}
                  title="Replacer le bouton ? en bas a droite"
                  style={{
                    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '4px 10px', fontSize: 11,
                    fontFamily: "'IBM Plex Sans', sans-serif",
                  }}
                >Reposition</button>}
                <button onClick={() => setOpen(false)} aria-label="Fermer l'aide" style={{
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 6, color: '#fff', cursor: 'pointer', padding: 4, display: 'flex',
                }}>
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {/* Aide poussée par la page (ex. exigence sélectionnée) : en tête, avant tout le reste */}
              {aideContextuelle && <BlocContextuel aide={aideContextuelle} />}

              {/* « Premiers pas » : internes seulement ; « Reprendre… » si le compte l'a laissé en cours */}
              {visite && visite.estInterne && (
                <BoutonVisite
                  titre={visite.etatPremiersPas?.etat === 'en_cours' ? 'Reprendre les Premiers pas' : 'Premiers pas'}
                  sousTitre={visite.etatPremiersPas?.etat === 'en_cours'
                    ? (visite.chapitreEnCours ? `Au chapitre ${visite.chapitreEnCours}` : 'Là où tu t\'es arrêté')
                    : 'Visite guidée du portail (≈ 10 min)'}
                  onClick={() => { setOpen(false); setTimeout(() => visite.lancerPremiersPas(), 300); }}
                />
              )}

              {/* Visite de la page : seulement si la page en a une (plus de repli vers la visite générale) */}
              {visite && visite.visitePageDisponible && (
                <BoutonVisite
                  titre="Visite de cette page"
                  sousTitre="Parcours interactif de cette section"
                  onClick={() => { setOpen(false); setTimeout(() => visite.lancerVisitePage(), 300); }}
                />
              )}

              {/* Bouton Signaler un bug — TOUJOURS visible (interne uniquement) */}
              {!reportOpen && !featureOpen && (
                <button
                  onClick={() => { setReportOpen(true); setFeatureOpen(false); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', marginBottom: 10,
                    background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                    border: '1px solid #fecaca', borderRadius: 8, cursor: 'pointer',
                    fontFamily: "'IBM Plex Sans', sans-serif", transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2, #fecaca)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2, #fee2e2)'; }}
                >
                  <Bug size={16} color="#b91c1c" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#b91c1c' }}>Signaler un bug</div>
                    <div style={{ fontSize: 11, color: '#7f1d1d' }}>Capture le contexte technique + envoi à l'équipe</div>
                  </div>
                </button>
              )}

              {/* Bouton Proposer une idée — TOUJOURS visible (interne uniquement) */}
              {!reportOpen && !featureOpen && (
                <button
                  onClick={() => { setFeatureOpen(true); setReportOpen(false); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', marginBottom: 16,
                    background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                    border: '1px solid #fde68a', borderRadius: 8, cursor: 'pointer',
                    fontFamily: "'IBM Plex Sans', sans-serif", transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fef3c7, #fde68a)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fffbeb, #fef3c7)'; }}
                >
                  <Lightbulb size={16} color="#b45309" />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#b45309' }}>Proposer une idée</div>
                    <div style={{ fontSize: 11, color: '#92400e' }}>Suggérer une amélioration ou une fonctionnalité</div>
                  </div>
                </button>
              )}

              {/* Formulaire bug inline */}
              {reportOpen && (
                <BugReportInlineForm
                  onClose={() => setReportOpen(false)}
                />
              )}

              {/* Formulaire idée inline */}
              {featureOpen && (
                <FeatureRequestInlineForm
                  onClose={() => setFeatureOpen(false)}
                />
              )}

              {/* Avec une aide contextuelle, l'aide générale de l'écran reste à portée, repliée */}
              {help && aideContextuelle && (
                <button
                  type="button"
                  data-testid="aide-generale-bascule"
                  aria-expanded={generaleOuverte}
                  onClick={() => setGeneraleOuverte((o) => !o)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', marginBottom: 8,
                    display: 'flex', alignItems: 'center', gap: 6, color: 'var(--primary)', fontWeight: 700, fontSize: 12,
                    fontFamily: "'IBM Plex Sans', sans-serif",
                  }}
                >
                  {generaleOuverte ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  Aide générale de l'écran
                </button>
              )}

              {/* User sections — affichées uniquement si help défini pour cette route */}
              {help && (!aideContextuelle || generaleOuverte) && (help.user || []).map((section, i) => (
                <HelpSection key={`u-${i}`} section={section} />
              ))}

              {/* Admin sections — superadmin uniquement */}
              {help && (!aideContextuelle || generaleOuverte) && isSuperAdmin && (help.admin || []).length > 0 && (
                <AdminBlock sections={help.admin} />
              )}

              {/* Placeholder si aucune aide définie pour cette route */}
              {!help && !aideContextuelle && !reportOpen && !featureOpen && (
                <div style={{
                  fontSize: 12, color: 'var(--text-muted)', textAlign: 'center',
                  padding: '20px 10px', fontStyle: 'italic',
                }}>
                  Pas d'aide spécifique pour cette page. Tu peux quand même signaler
                  un bug avec le bouton ci-dessus.
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '10px 20px', borderTop: '1px solid var(--border-light)',
              fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <HelpCircle size={10} />
              Aide contextuelle — {isSuperAdmin ? 'mode admin' : 'mode utilisateur'}
            </div>
          </div>
      )}

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}

// ── Bouton de lancement d'une visite guidée (même style pour les deux) ───────

function BoutonVisite({ titre, sousTitre, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', marginBottom: 12,
        background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
        border: '1px solid #bae6fd', borderRadius: 8, cursor: 'pointer',
        fontFamily: "'IBM Plex Sans', sans-serif", transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #e0f2fe, #bae6fd)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #f0f9ff, #e0f2fe)'; }}
    >
      <span style={{ fontSize: 18 }}>▶</span>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#0369a1' }}>{titre}</div>
        <div style={{ fontSize: 11, color: '#0c4a6e' }}>{sousTitre}</div>
      </div>
    </button>
  );
}

// ── Aide contextuelle (poussée par la page, voir AideContextuelle.js) ────────

function BlocContextuel({ aide }) {
  return (
    <div
      data-testid="aide-contextuelle"
      style={{
        marginBottom: 16, padding: '12px 14px', borderRadius: 8,
        background: '#f5f9fd', border: '1px solid #c7dbef',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          {aide.sousTitre && (
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {aide.sousTitre}
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--primary)', marginTop: 2 }}>{aide.titre}</div>
        </div>
        {aide.quitter && (
          <button
            type="button"
            onClick={aide.quitter}
            title="Revenir à l'aide générale de l'écran"
            aria-label="Revenir à l'aide générale"
            style={{
              background: 'none', border: '1px solid var(--border-light)', borderRadius: 6, cursor: 'pointer',
              padding: 3, display: 'flex', color: 'var(--text-muted)',
            }}
          >
            <X size={12} />
          </button>
        )}
      </div>
      {(aide.sections || []).map((section, i) => (
        <HelpSection key={`c-${i}`} section={section} />
      ))}
    </div>
  );
}

// ── Help section (always visible) ────────────────────────────────────────────

function HelpSection({ section }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontWeight: 700, fontSize: 13, color: 'var(--primary)',
        marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {section.heading}
      </div>
      <div style={{
        fontSize: 12, color: 'var(--text)', lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
      }}>
        {section.content && <FormattedText text={section.content} />}
        {Array.isArray(section.items) && section.items.length > 0 && (
          <ul style={{ margin: section.content ? '6px 0 0' : 0, paddingLeft: 18, whiteSpace: 'normal' }}>
            {section.items.map((item, i) => <li key={i} style={{ marginBottom: 3 }}><FormattedText text={item} /></li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Admin block (superadmin only, collapsible) ───────────────────────────────

function AdminBlock({ sections }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      marginTop: 12, borderTop: '1px solid var(--border-light)', paddingTop: 12,
    }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          marginBottom: expanded ? 12 : 0,
        }}
      >
        <Shield size={14} style={{ color: '#8e44ad' }} />
        <span style={{
          fontWeight: 800, fontSize: 10, color: '#8e44ad',
          textTransform: 'uppercase', letterSpacing: '0.08em',
          fontFamily: "'IBM Plex Mono', monospace",
        }}>
          Details techniques & logique metier
        </span>
        {expanded
          ? <ChevronDown size={12} style={{ color: '#8e44ad', marginLeft: 'auto' }} />
          : <ChevronRight size={12} style={{ color: '#8e44ad', marginLeft: 'auto' }} />
        }
      </button>

      {expanded && (
        <div style={{
          background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8,
          padding: '12px 14px',
        }}>
          {sections.map((section, i) => (
            <div key={`a-${i}`} style={{ marginBottom: i < sections.length - 1 ? 14 : 0 }}>
              <div style={{
                fontWeight: 700, fontSize: 12, color: '#6b21a8',
                marginBottom: 4,
              }}>
                {section.heading}
              </div>
              <div style={{
                fontSize: 11, color: '#581c87', lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                fontFamily: section.content.includes('`') ? "'IBM Plex Mono', monospace" : 'inherit',
              }}>
                <FormattedText text={section.content} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Simple markdown-ish formatter ────────────────────────────────────────────

function FormattedText({ text }) {
  if (!text) return null;

  // Split by ** for bold, ` for code
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} style={{ fontWeight: 700, color: 'var(--primary)' }}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code key={i} style={{
              background: 'var(--bg)', padding: '1px 4px', borderRadius: 3,
              fontSize: '0.9em', fontFamily: "'IBM Plex Mono', monospace",
              color: '#c0392b',
            }}>
              {part.slice(1, -1)}
            </code>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}
